// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The wrist menu (M5-24, M5-25, XR-009).
//
// Opened by turning the left palm face-up and holding, or by squeezing either grip. Six items,
// one of which can end your game and therefore arms before it fires.
//
// **The gesture set is inherited rather than invented.** 06 §5: "the identical gesture set Chess
// VR settled on after several device builds, so it is proven rather than guessed." Palm-up is the
// one hand gesture that a player never makes by accident while playing, because playing pinball
// is done with the palms *down* on the cabinet rails — which is a happy accident of this
// particular game and is worth saying out loud, since it is the reason the 250 ms hold can be as
// short as it is.
//
// **Arm-and-confirm, not a dialogue.** A confirmation dialogue in a headset is a thing to read
// while wearing a headset. Arming is the same button twice inside three seconds: the label
// changes to say what the second press will do, and it disarms itself if you walk away. This is
// Chess VR's resign pattern and it costs one field.
//
// **The menu does not know what the items do.** It reports a selection; the shell performs it.
// A menu that paused the simulation directly would be a second owner of the game's state, and the
// one thing this codebase is careful about is having one owner for each thing.
//
// **Three kinds of row, and the difference between them is whether the menu stays open** (M8-09).
// A row that takes the player somewhere — Comfort, Machine, Exit XR — closes, because the menu has
// done its job and a menu left hanging in front of a live ball is in the way. A row that arms
// stays open, because a confirmation the player cannot see is not a confirmation. And a row that
// **toggles a state the row itself displays** stays open too: the whole feedback for pressing
// "Microphone on" is that it now reads "Microphone off", and closing the panel throws that away
// and leaves the player guessing about the one control in this product they most need to be sure
// of. See `TOGGLES`.

import { micLabel, type MicrophoneAccess } from '../audio/voiceMute'
import { captionsLabel, masterLabel, soundLabel, type AudioLevel } from '../audio/audioPrefs'
import { flashingLabel, heightLabel, motionLabel, type HeightOffset } from '../client/comfortPrefs'
import type { FlashingChoice } from '../client/accessibility'
import { benchStringsFor, benchText, type BenchLanguage } from './build/benchStrings'
import { speedText } from './build/testTools'

/** The six items of 06 §5, plus the destructive one and the two mutes they contain. */
export type MenuItem =
  | 'pauseResume'
  | 'machine'
  | 'scaleAndPlace'
  | 'comfort'
  | 'sound'
  | 'exitXr'
  /** Inside `machine`, and the reason arm-and-confirm exists. */
  | 'endGame'
  /**
   * Inside `sound` (06 §5: "Master volume, mute, mute voice chat, callout captions"), and drawn
   * on the root page **as well as** on that one (M8-09).
   *
   * **A mute control that takes two presses to reach is not a mute control.** The moment a player
   * needs their microphone off is the moment they have least patience for a submenu, and 06 §4
   * asks for "a mute control in the wrist menu" rather than a route to one. So these two are
   * hoisted: they appear on `sound`, where 06 §5 puts them, and again on the root list, which is
   * where a hand goes for them. The duplication is the feature.
   */
  | 'muteMic'
  | 'muteVoice'
  /**
   * The other three rows of 06 §5's Sound list, none of which a player could reach (M8-23).
   *
   * The row said "Sound on", pressing it muted the machine, and that was the whole of it — so of
   * the four things the requirement names, two existed (`muteAll` under another name, and
   * `muteVoice`) and two did not.
   *
   * **`masterVolume` had an implementation and no way in.** `Mixer.setMasterVolume` has been a
   * correct, tested, third input to the master gain since M3-20 with no caller anywhere in the
   * product, and the C# mixer had no master level at all. See `AUDIO_MASTER_KEY`.
   *
   * **`captions` had a preference and no headset control.** NFR-022 asks for captions "on screen
   * *and in the headset*"; the flat client has had a settings field since M7-47, and a player in a
   * headset had no way to turn them off — or, having turned them off on a phone, to find out in
   * words why the machine had stopped telling them things.
   *
   * `muteAll` is the row `sound` used to be, and it keeps `soundLabel`'s exact words: this moves
   * where it is drawn, not what it says.
   */
  | 'masterVolume'
  | 'muteAll'
  | 'captions'
  /**
   * Leave the arcade for the machine picker (M8-20).
   *
   * On the machine page rather than the root, because it is the *other* answer to "Machine": one
   * of these six, or a different machine altogether. It is also the only row here that ends the
   * immersive session, which is a reason to keep it away from the row a player reaches for while
   * a ball is in play.
   */
  | 'otherMachine'
  /** One machine in the venue's row. Six of them, because the venue has six bays (06 §4). */
  | 'bay1'
  | 'bay2'
  | 'bay3'
  | 'bay4'
  | 'bay5'
  | 'bay6'
  /**
   * The five rows 06 §5 lists behind Scale & Place: "Full / Tabletop toggle, Re-place, Face Me,
   * Recenter, 5° rotation nudges".
   *
   * **They existed and could not be reached, which is worse than not existing.** Every one of them
   * was implemented and tested on both clients — `setScaleMode`, `replace`, `faceMe`, `recenter`,
   * `rotateBy` — and none had a caller. A player whose machine ended up somewhere wrong had no way
   * to move it, so every placement defect was permanent for the session (device report,
   * 25 Aug 2026).
   */
  | 'scaleMode'
  | 'replaceMachine'
  | 'faceMe'
  | 'recenterMachine'
  | 'nudgeLeft'
  | 'nudgeRight'
  /**
   * Throw away the remembered spot entirely — the anchor, the scale and the angle.
   *
   * Destructive, and the only destructive row outside `endGame`. "Re-place" keeps what the player
   * chose and asks only where; this is for the case where what they chose is itself wrong, which
   * on this hardware happens when a room is re-scanned under a placement made in the old one.
   */
  | 'forgetSpot'
  /** Back to the root list. A page with no way out is a menu a player has to close and reopen. */
  | 'back'
  /**
   * Decline the high-score table (M2-19a).
   *
   * **Only on the list while the machine is asking**, which makes it the second row whose presence
   * is conditional — `bayCount` already removes the bay rows in mixed reality, and the argument is
   * the same one: a row that cannot do anything is a row that costs a player a press to find out.
   * Outside high-score entry `Game.initialsSkip` is a no-op, so a permanent row would be dead for
   * all but a few seconds of a session and would sit in a list that has been curated down to six.
   *
   * **Appended rather than placed**, so that every row a player already knows keeps its index. The
   * hit test and the paint both index this list, and moving Pause to make room for something that
   * appears once a game would be paying for it in the wrong place.
   *
   * **Not `DESTRUCTIVE`**, deliberately, and it is the closest call in that set. Arm-and-confirm
   * exists for `endGame` and `forgetSpot`, which throw away a game and a placement; this declines
   * a *name* on a row the player still gets — the score keeps its place with the marker on it. It
   * is also one press on the flat screen, and a headset that asked twice for the same decision
   * would be a second answer to a question the product has already settled.
   */
  | 'skipInitials'
  /**
   * The four rows behind Comfort (G2-03; 06 §6).
   *
   * **Comfort was one row for six commitments.** 06 §6 lists seated mode, reduce flashing, insert
   * legibility, callout captions, audio description, one-handed play and an adjustable machine
   * height; the row called Comfort toggled seated mode and nothing else. Two of those had a
   * preference the flat client could set and no headset control at all, and the height had no
   * implementation anywhere — `grep -rn "heightOffset\|height offset\|HeightOffset" src/ native/
   * test/` returned nothing on 2026-09-03.
   *
   * This is the same move M8-23 made for Sound and M8-20 made for Machine, for the third time and
   * for the same reason: a root row named after a *category* that performs one member of it is a
   * row whose name is a promise the panel does not keep.
   *
   * `seatedMode` is the toggle the old `comfort` row was, keeping its behaviour exactly — this
   * moves where it is drawn, not what it does.
   */
  | 'seatedMode'
  | 'reduceFlashing'
  | 'reduceMotion'
  | 'heightOffset'
  /**
   * Build mode (M13-09, 06 §9, ADR 0015) — **web-only, and behind `VITE_PINBALL_BUILD_MODE`.**
   *
   * `build` is a root row that enters the mode; the six below are the Build page, which is the
   * menu's home page for as long as the mode is on (see `WristMenu.home`). None of the seven is
   * in `MENU_ITEMS`, `PAGING`, `TOGGLES` or `DESTRUCTIVE`, and `rowsFor` draws them only when
   * asked to, so the parity fixture the native client replays does not change: the Quest has no
   * Build mode yet, and a fixture that named a row it cannot draw would fail it for a feature it
   * was never asked for. `menu-parity.test.ts` records the seven as `WEB_ONLY_ROWS`, the mirror
   * of its `NATIVE_ONLY_ROWS`.
   */
  | 'build'
  | 'undo'
  | 'redo'
  | 'symmetry'
  | 'testPlay'
  | 'keep'
  | 'leaveBuild'
  /**
   * The machine put back as it was when Build began (the 1.0.115 round, 27 Sep 2026: "user should
   * have option (with warning before actually resetting machine) to reset build machine back to
   * original pinball machine"). Arms on the first press — the row and a toast say what the second
   * press does — and lands as one step Undo takes back. `WristMenu.resettableBuild` says whether
   * there is anything to reset.
   */
  | 'resetMachine'
  /**
   * The Build page's page of the two rows pressed least (the 1.0.115 round): Symmetry and Reset,
   * one press in, so the Build page stays seven rows a finger can hit and the row that throws
   * work away is not next to the rows pressed most.
   */
  | 'buildOptions'
  /** Persistent application navigation and contextual quick-menu handoffs. */
  | 'fullMenu'
  | 'currentRules'
  | 'help'
  | 'tutorial'
  | 'account'
  | 'close'
  /**
   * The test page's rows (M13-25): the Builder's own test tools, from the wrist, while a draft
   * is under Test play. `freeze`, `step`, `slowMotion` and `trail` leave the menu open (the
   * build toggles); `placeBall` closes it, because the next pinch on the board is the placement.
   * `addBall` (26 Sep 2026, "a second ball in a test") closes it for the same reason: the next
   * pinch on the board is where one more ball goes.
   */
  | 'freeze'
  | 'step'
  | 'slowMotion'
  | 'placeBall'
  | 'addBall'
  | 'trail'
  /**
   * MR ↔ VR from inside a session (14 Sep 2026) — **never both at once.**
   *
   * "The user should have a way to switch from MR <> VR in MR/VR." VR is a room drawn inside the
   * passthrough session (15 Sep 2026), so the row puts the arcade up or takes it down in place —
   * `XRRuntime.setLook` — and the game, a Build draft and the anchor all stay, because nothing
   * ends. One row, named for the look it goes to — `switchToVr` in MR, `switchToMr` in VR — and
   * drawn only in a session that has passthrough (`MenuState.switchTo`). **In the fixture since
   * 18 Sep 2026** (Phase 3, P3-03): the native client had its own row for this act (`seeMyRoom`,
   * first on the place page, since M8-30), and the owner chose this one for both wrists — root
   * page, directly above Exit, the action as the label — so the headset's retired.
   */
  | 'switchToVr'
  | 'switchToMr'
  /**
   * **The short-page rows** (the 1.0.111 round, 27 Sep 2026: "the left-palm menu buttons are very
   * small because there is too many things on each left-palm menu … maybe multiple menus"). The
   * root list had grown to twelve rows — 17 mm a button on a 22 cm panel — so the root keeps the
   * rows a hand reaches for mid-ball and pages the rest: `game` (pause, end, machines), `settings`
   * (sound, comfort, the two voice mutes), `position` (the adjustments under Room), and `time`
   * (freeze, step, speed under Test play). Every page is six rows or so, 30 mm or more a button.
   */
  | 'game'
  | 'settings'
  | 'position'
  | 'time'

/** Which mode the wrist can switch to: `'vr'` from mixed reality, `'mr'` from VR (14 Sep 2026). */
export type SwitchTarget = 'vr' | 'mr'

/** The shallow play menu. Builder is offered only when the host supports authoring. */
export const MENU_ITEMS: readonly MenuItem[] = ['pauseResume', 'otherMachine', 'game', 'build', 'scaleAndPlace', 'settings', 'help', 'close']

export type MenuPresentation = 'quick' | 'normal'

/** The bay rows, in bay order. Indexed by bay, so `BAY_ITEMS[2]` is machine three. */
export const BAY_ITEMS: readonly MenuItem[] = ['bay1', 'bay2', 'bay3', 'bay4', 'bay5', 'bay6']

/** Which bay an item names, or −1. */
export function bayOfItem(item: MenuItem): number {
  return BAY_ITEMS.indexOf(item)
}

/** Which list the menu is showing. */
export type MenuPage =
  | 'root'
  | 'bays'
  | 'place'
  | 'sound'
  | 'comfort'
  | 'build'
  | 'test'
  | 'game'
  | 'settings'
  | 'position'
  | 'time'
  | 'buildOptions'
  | 'help'
  | 'departure'

/**
 * Where Back goes from a page (the 1.0.111 round): one level up, now that pages nest. Sound and
 * Comfort sit under Settings, Position under Room, the machine list under Game, and the time
 * tools under the test page. Every other page's Back is the root, as it always was.
 */
export const BACK_TO: ReadonlyMap<MenuPage, MenuPage> = new Map<MenuPage, MenuPage>([
  ['sound', 'settings'],
  ['comfort', 'settings'],
  ['position', 'place'],
  ['bays', 'position'],
  ['game', 'root'],
  ['time', 'test'],
  ['buildOptions', 'build'],
])

/** The page Back leads to from `page`. */
export function backFrom(page: MenuPage): MenuPage {
  return BACK_TO.get(page) ?? 'root'
}

/**
 * The Build page, in the order a hand reaches for them (M13-09).
 *
 * Undo and Redo first, because they are pressed most and pressed in pairs; Symmetry next, a
 * setting the carry reads on every frame; then the two that leave the page — Test play grows the
 * machine, Keep saves it — and the way out. `back` reaches the ordinary root for Sound and
 * Comfort, which an author still needs while building.
 */
export const BUILD_ROWS: readonly MenuItem[] = [
  'undo',
  'redo',
  'testPlay',
  'keep',
  'buildOptions',
  'leaveBuild',
  'back',
]

/** Symmetry and Reset, one press in from the Build page (the 1.0.115 round); Back is the Build page. */
export const BUILD_OPTIONS_ROWS: readonly MenuItem[] = ['symmetry', 'resetMachine', 'back']

export const QUICK_BUILD_ROWS: readonly MenuItem[] = ['undo', 'redo', 'testPlay', 'keep', 'fullMenu']
export const QUICK_TEST_ROWS: readonly MenuItem[] = ['testPlay', 'freeze', 'placeBall', 'addBall', 'fullMenu']
export const QUICK_TIME_ROWS: readonly MenuItem[] = ['step', 'slowMotion', 'trail', 'back']

/**
 * Build-page rows that leave the menu open, for `TOGGLES`'s reasons and kept apart from it.
 *
 * Undo and Redo are pressed more than once by design (the `nudgeLeft` argument), and Symmetry
 * states the condition it flips. **Not in `TOGGLES`** because that set is written into the parity
 * fixture and replayed by the native client, which has none of these rows; `select` consults both.
 */
export const BUILD_TOGGLES: ReadonlySet<MenuItem> = new Set<MenuItem>(['undo', 'redo', 'symmetry', 'freeze', 'step', 'slowMotion', 'trail'])

/**
 * Full and Quick Test share the same task order: return to editing, Freeze/Run,
 * ball tools, then the finer Test tools. Full adds its own navigation below them.
 */
export const TEST_ROWS: readonly MenuItem[] = ['testPlay', 'freeze', 'placeBall', 'addBall', 'time', 'back']

/**
 * Step, speed and ball trail share the Test tools child. Freeze/Run stays directly
 * on Test so both Full and Quick expose the same first actions.
 */
export const TIME_ROWS: readonly MenuItem[] = ['step', 'slowMotion', 'trail', 'back']

/**
 * Items that change the page rather than doing something (M8-20).
 *
 * Like `TOGGLES`, they leave the menu open — a page that closed the menu on the way to itself
 * would be a page nobody ever saw.
 *
 * **`machine` pages instead of leaving, and that is 06 §5 read literally**: "Machine — return to
 * the arcade, **or pick another machine**". It used to do only the first half, which meant the one
 * row named after the thing a player wants to change was the row that ended their session to
 * change it. The other half is now the list behind it, and leaving is a row on that list.
 */
export const PAGING: ReadonlyMap<MenuItem, MenuPage> = new Map<MenuItem, MenuPage>([
  ['machine', 'bays'],
  // **`scaleAndPlace` pages rather than toggling the scale, which is 06 §5 read literally**:
  // "Scale & Place — Full / Tabletop toggle, Re-place, Face Me, Recenter, 5° rotation nudges".
  // It used to do only the first of the five, and the note that sat here said the other four were
  // "the submenu's, and the submenu is a shell surface rather than a wrist row". There is no such
  // surface in a headset. This is that submenu.
  ['scaleAndPlace', 'place'],
  // **`sound` pages for the reason `scaleAndPlace` does, out of the same table**: 06 §5 reads
  // "Sound — Master volume, mute, mute voice chat, callout captions", and the row did one of the
  // four. The alternative was eleven rows on the root list, five of them about audio, on a panel
  // whose own header worries about six.
  ['sound', 'sound'],
  // **`comfort` pages for the third time in this table and out of the same reading.** 06 §6 names
  // seven accessibility commitments and the row did one of them. See `seatedMode`.
  ['comfort', 'comfort'],
  // The short pages (the 1.0.111 round). `back` answers `backFrom` in `select`; the root is its
  // default and the entry here says so.
  ['game', 'game'],
  ['settings', 'settings'],
  ['position', 'position'],
  ['time', 'time'],
  ['buildOptions', 'buildOptions'],
  ['help', 'help'],
  ['back', 'root'],
])

/**
 * The rows a page draws, in order.
 *
 * **Here rather than in the panel**, because the hit test and the paint have to agree about what
 * is in row four and they are two functions in two files. One list, asked twice.
 *
 * The three items that live *inside* another one — End Game under Machine, and the two mutes under
 * Sound (06 §5) — are drawn directly on the root page: a wrist panel has no room for a second
 * level of nesting, and a mute that needs two presses to reach is not a mute (see `MenuItem`).
 * The machine list is the one exception, and it earns it by being six rows that cannot be folded
 * into anything.
 */
export function rowsFor(
  page: MenuPage,
  bayCount = BAY_ITEMS.length,
  /**
   * Is the machine asking for three characters right now (M2-19a)?
   *
   * A second reason the row list is not a constant, and it works the way `bayCount` does rather
   * than by a new mechanism: the caller knows what the machine is doing, and the menu is the thing
   * that turns that into rows.
   */
  enteringInitials = false,
  /**
   * Is there a machine in the room for these rows to be about (M8-63)?
   *
   * ***Three rows here are sentences about a game, and a launcher board takes the game away.***
   * The Quest removes the machine from the room outright whenever a board is up — M8-59 does
   * that deliberately, because a board and a cabinet cannot share the space in front of a player
   * — and the wrist panel went on offering Pause, End game and Skip initials over an empty room.
   * The 1 Sep 2026 session is what that cost: **five armed-and-confirmed presses of End game**,
   * every one of them landing, and the only thing that happened each time was the menu closing.
   * Reported as *"clicking on end game then disappears before user can click 2nd confirmation"*,
   * which is exactly what a confirmed press with nothing behind it looks like.
   *
   * `exitXr` deliberately stays. In a browser it ends the immersive session and on a Quest it is
   * the way back to the launcher; either way it is the row that gets a player out, and a menu with
   * no exit is worse than one with a row that repeats itself.
   *
   * **Defaulted true, like the C# field it mirrors.** Every caller that has a machine has one;
   * this is the exception, and a default of false would silently hide three rows from any fixture
   * that had not been updated.
   */
  machineInPlay = true,
  /**
   * Is Build mode offered — is `VITE_PINBALL_BUILD_MODE` on (M13-09)?
   *
   * Defaulted off, so every caller that predates it — the parity fixture above all — draws the
   * rows it always drew. The panel passes `state.build !== undefined`.
   */
  buildOffered = false,
  /**
   * Which mode the wrist can switch to, or nothing (14 Sep 2026). `'vr'` from MR, `'mr'` from VR;
   * undefined when the runtime has not got the other mode, and on a flat screen. Defaulted off,
   * so the parity fixture draws the rows it always drew. The panel passes `state.switchTo`.
   */
  switchTo?: SwitchTarget,
  presentation: MenuPresentation = 'normal',
  voiceAvailable = false,
): readonly MenuItem[] {
  if (presentation === 'quick') return quickRowsFor(page, enteringInitials, machineInPlay, voiceAvailable, switchTo)
  if (page === 'build') return BUILD_ROWS
  if (page === 'test') return TEST_ROWS
  if (page === 'time') return TIME_ROWS
  if (page === 'buildOptions') return BUILD_OPTIONS_ROWS
  // Current-game actions have a direct Home destination; Arcade remains the picture gallery.
  // Spatial bay navigation stays a separate, preserved session capability.
  if (page === 'departure') return ['keep', 'leaveBuild', 'back']
  if (page === 'game') {
    return [...(machineInPlay ? (['currentRules', 'endGame'] as const) : []), 'back']
  }
  // Settings owns preferences and account utilities; current-game actions stay outside it.
  if (page === 'settings') return ['sound', 'comfort', 'account', 'exitXr', 'back']
  if (page === 'help') return [...(machineInPlay ? (['currentRules'] as const) : []), 'tutorial', 'back']
  // **Position**: the adjustments that were the bulk of the place page, one level under Room.
  if (page === 'position') {
    return ['faceMe', 'nudgeLeft', 'nudgeRight', 'replaceMachine', 'forgetSpot', ...(bayCount > 0 ? ['machine' as const] : []), 'back']
  }
  // **`bayCount` is 0 in mixed reality and that is the whole point of the parameter.** There is no
  // row of machines in somebody's living room — there is one cabinet — so the page is the two rows
  // that still mean something rather than six that lead nowhere.
  if (page === 'bays') return [...BAY_ITEMS.slice(0, bayCount), 'back']
  // Recovery leads Room; supported world switching, scale and fine position follow it.
  if (page === 'place') {
    return [
      'recenterMachine',
      ...(switchTo === undefined ? [] : ([switchTo === 'vr' ? 'switchToVr' : 'switchToMr'] as const)),
      'scaleMode',
      'position',
      'back',
    ]
  }
  // 06 §5's four, in the order it lists them. The two hoisted rows are drawn here as well as on
  // the root list — this is where they live, and root is where a hand reaches for them.
  if (page === 'sound') {
    return ['masterVolume', 'muteAll', 'muteMic', 'muteVoice', 'captions', 'back']
  }
  // 06 §6's controls, seated mode first because it is the one this row has always been.
  //
  // **Every one of these states its own condition**, which is what puts three of them in `TOGGLES`:
  // pressing a row changes the sentence the row is showing, and a panel that closed on the press
  // would throw away the only feedback a player gets. Seated mode is the exception in shape and not
  // in kind — it repositions the machine, which is feedback a player cannot miss — and it is a
  // toggle here anyway, because the alternative is the one row on this page that behaves
  // differently from its neighbours.
  if (page === 'comfort') {
    return ['seatedMode', 'reduceFlashing', 'reduceMotion', 'heightOffset', 'back']
  }
  return MENU_ITEMS.filter((item) => (machineInPlay || item !== 'pauseResume') && (buildOffered || item !== 'build'))
    .map((item) => item === 'pauseResume' && enteringInitials ? 'skipInitials' : item)
}

/** Quick tools are deliberately not another application launcher. */
export function quickRowsFor(page: MenuPage, enteringInitials = false, machineInPlay = true, voiceAvailable = false, switchTo?: SwitchTarget): readonly MenuItem[] {
  const withSwitch = (items: readonly MenuItem[]): readonly MenuItem[] => switchTo === undefined ? items : [...items.slice(0,-1), switchTo === 'vr' ? 'switchToVr' : 'switchToMr', items[items.length-1]!]
  if (page === 'build') return withSwitch(QUICK_BUILD_ROWS)
  if (page === 'test') return withSwitch(QUICK_TEST_ROWS)
  if (page === 'time') return withSwitch(QUICK_TIME_ROWS)
  return [
    ...(machineInPlay ? ([enteringInitials ? 'skipInitials' : 'pauseResume'] as const) : []),
    'muteAll',
    ...(voiceAvailable ? (['muteMic', 'muteVoice'] as const) : []),
    ...(switchTo === undefined ? [] : [switchTo === 'vr' ? 'switchToVr' as const : 'switchToMr' as const]),
    'fullMenu',
  ]
}

/** Items that end something a player cannot get back. */
export const DESTRUCTIVE: ReadonlySet<MenuItem> = new Set<MenuItem>(['endGame', 'forgetSpot'])

/**
 * Items that can arm: the destructive ones always, and Leave build while the bench has edits
 * nothing has kept (M13-15). The label guards walk this set for the armed variant of a row, so an
 * "armed" sentence the panel can show is a sentence the parity fixture has seen.
 */
export const ARMABLE: ReadonlySet<MenuItem> = new Set<MenuItem>([...DESTRUCTIVE, 'leaveBuild', 'resetMachine'])

/**
 * Items that flip a state the row itself is showing, and therefore leave the menu open (M8-09).
 *
 * The mic mute is the one that earns this rule. A player presses it because somebody walked into
 * the room; if the panel vanishes on the press, the only evidence that anything happened is that
 * nobody said "you're still on" — which is evidence that arrives too late to be worth anything.
 *
 * **`sound` joined them late, and the rule had already decided it** (M5-34). It was written when
 * the row said the bare word "Sound" and pressing it opened nothing — a row that displayed no
 * state could not be a row that displayed the state it changed. 07 §7 then gave it
 * `soundLabel(soundMuted)`, which is a sentence about a condition, and the membership test above
 * was suddenly true of it without anybody moving it. The symptom was the ordinary one: a player
 * mutes the machine, the panel disappears, and the only confirmation that anything happened is
 * that a machine they are not currently touching has gone quiet.
 */
export const TOGGLES: ReadonlySet<MenuItem> = new Set<MenuItem>([
  // **`sound` itself is no longer here, and it did not lose the rule — it stopped being a
  // control.** It pages now, and `PAGING` already leaves the menu open. What it had to keep is the
  // sentence M5-34 fought for: its label still says when the machine is silent, so a player who
  // muted in VR and came back is not reading a row that tells them nothing. See `labelFor`.
  'muteAll',
  'masterVolume',
  'captions',
  'muteMic',
  'muteVoice',
  // **The place page's adjustments, which is a second reason to stay open and worth naming.** The
  // three above leave the menu up because their own row displays the state they flipped. These
  // leave it up because they are meant to be pressed more than once: a 5° nudge that closed the
  // panel would need seven reopenings to turn a cabinet a quarter turn, and a scale a player is
  // comparing is a scale they want to flip back.
  'scaleMode',
  'nudgeLeft',
  'nudgeRight',
  // 06 §6's comfort rows (G2-03). Each states the condition it flips, which is this set's whole
  // membership test — and the height row is additionally one a player presses more than once,
  // which is the `nudgeLeft` argument: an offset that closed the panel would need four reopenings
  // to move from one end of its range to the other.
  'seatedMode',
  'reduceFlashing',
  'reduceMotion',
  'heightOffset',
])

/**
 * How long a palm must be up before the menu opens, milliseconds.
 *
 * 250. Long enough that turning a hand over while reaching for the plunger does not open it;
 * short enough that it feels like a gesture rather than a wait.
 */
export const PALM_HOLD_MS = 250

/**
 * How far from straight up a palm may point and still open the menu, as a dot against up.
 *
 * 0.55 ≈ 57°. It was 0.72 ≈ 44°, "generous, because palm up is a posture rather than a
 * measurement and nobody holds their hand level" — and then the palm number was made to measure
 * the palm (a2ff4bb), and a palm turned to be *read* is turned toward the face, not the ceiling.
 * Measured on a Quest 3 across four sessions on 13–14 Sep 2026: a flat palm reads 0.78–0.92, and
 * the palm a person actually holds their menu on reads 0.63–0.79, sitting on the old line —
 * `palmUp 0.72 | menu closed`, `0.71 | closed`, `0.74 | open`, `0.69 | open`, `0.37 | closed`,
 * `0.79 | open` in one twelve-second stretch, and "in build mode, the left-palm menu not
 * appearing" from the person holding it. A threshold on the posture people use is a coin toss.
 * 0.55 is under the lowest reading of that posture with a tracking wobble to spare, and still
 * well above a hand at a flipper button (about 0 to 0.3) or reaching across the board.
 * Parity-shared with `WristMenu.cs` through `wrist-menu-v1.json`; a change is both clients.
 */
export const PALM_UP_ENTER = 0.55

/**
 * How far it has to fall before the menu closes again — **a second, lower threshold**.
 *
 * There was one threshold, and one threshold is a menu that flickers. A hand held near the
 * boundary crosses it many times a second from tracking noise alone, and the asymmetry made it
 * worse rather than better: opening needs 250 ms of dwell, closing took **one frame**. A single
 * sample 0.001 below the line shut the panel a player was looking at, and it then cost them a
 * quarter of a second to get it back. There is a test that drives exactly that frame.
 *
 * 0.35 ≈ 70°, so the palm must be turned decisively down rather than merely wobble — it moved
 * down with `PALM_UP_ENTER` (0.45 before), keeping a band of 0.20, twice the width of the
 * biggest wobble a held palm showed on the device (0.63–0.79 while reading the panel; one dip to
 * 0.37 as the other hand came across, which the old exit closed on). The width of the band is
 * taken from the chess client's `NativePalmGameMenu`, where `PalmUpEnter` and `PalmUpExit` were
 * tuned on a physical Quest — the values here are this product's own, measured on its own
 * headset, but the *existence* of a dead band is not a matter of taste.
 */
export const PALM_UP_EXIT = 0.35

/** How long an armed destructive item stays armed, milliseconds (06 §5: "~3 seconds"). */
export const ARM_WINDOW_MS = 3000

/** What the menu is showing. */
export interface MenuView {
  readonly open: boolean
  readonly presentation?: MenuPresentation
  /** Which list is showing. Reset to `root` whenever the menu closes. */
  readonly page: MenuPage
  /** The item currently armed, or undefined. Its label should say what a second press will do. */
  readonly armed: MenuItem | undefined
  /** Milliseconds left on the arming window, for a countdown ring. */
  readonly armedFor: number
}

/** A selection the shell should act on. */
export interface MenuSelection {
  readonly item: MenuItem
  /** True when this was the *confirming* press of a destructive item. */
  readonly confirmed: boolean
}

export class WristMenu {
  private openNow = false
  private pageNow: MenuPage = 'root'
  /**
   * The page the menu opens on and returns to when it closes (M13-09).
   *
   * `root`, always, until Build mode is entered; the shell sets `build` for as long as the mode
   * is on, so an author who turns their palm up mid-edit gets Undo under their finger rather than
   * Pause. Every path that used to reset to `root` — `toggle`, `close` — resets to this instead,
   * which is the whole change: the rule "reopened on the home list, always" is unchanged, and the
   * home list is now a fact about the mode rather than a literal.
   */
  private homeNow: MenuPage = 'root'
  private presentationNow: MenuPresentation = 'quick'
  private explicitSession = false
  private palmMs = 0
  private armedItem: MenuItem | undefined
  private armedMs = 0
  /** Latched so one squeeze is one toggle rather than one per frame. */
  private gripWasDown = false
  /** Opened by a grip? Then a palm turning over must not close it out from under a controller. */
  private openedByGrip = false
  /**
   * Closed from the inside while the palm was still up (M5-24a).
   *
   * **The menu used to reopen itself after every single selection.** A player turns their palm
   * up, the panel appears above it, they poke a row — and their palm is *still up*, because it
   * is holding the thing they just poked. 250 ms later the dwell completed again and the menu
   * came straight back, over the ball they had just unpaused. Cleared only by a palm that
   * actually turns over, which is the chess client's rule and the reason it has one.
   */
  private suppressed = false

  view(): MenuView {
    return {
      open: this.openNow,
      presentation: this.presentationNow,
      page: this.pageNow,
      armed: this.armedItem,
      armedFor: this.armedItem === undefined ? 0 : Math.max(0, ARM_WINDOW_MS - this.armedMs),
    }
  }

  get open(): boolean {
    return this.openNow
  }

  get presentation(): MenuPresentation { return this.presentationNow }

  /** The host's raised-hand session owns quick visibility; angle changes cannot dismiss it. */
  openQuick(): boolean {
    if (this.openNow && this.presentationNow === 'normal') return false
    if (!this.openNow || this.presentationNow !== 'quick') { this.pageNow = this.homeNow; this.disarm() }
    this.presentationNow = 'quick'; this.explicitSession = true; this.openNow = true
    return true
  }

  closeQuick(): void { if (this.presentationNow === 'quick') this.close() }

  openNormal(page: MenuPage = 'root'): void {
    this.presentationNow = 'normal'; this.explicitSession = true; this.openNow = true
    this.pageNow = page; this.palmMs = 0; this.disarm()
  }

  closeNormal(): void { if (this.presentationNow === 'normal') this.close() }

  get home(): MenuPage {
    return this.homeNow
  }

  /**
   * Make a page the home page. The shell calls this entering and leaving Build mode.
   *
   * Takes effect on the page now showing as well, so a menu left open on `build` while the mode
   * ends is not a menu showing rows that no longer do anything.
   */
  set home(page: MenuPage) {
    this.homeNow = page
    this.pageNow = page
    this.disarm()
  }

  /**
   * The bench has edits nothing has kept (M13-15): Leave build arms and confirms like the other
   * rows that end something a player cannot get back, and with nothing to lose it leaves at once.
   *
   * Told by the shell every frame, like `home`, because the answer changes with every commit and
   * every Keep, and the menu has no draft of its own to ask.
   */
  set unsavedBuild(on: boolean) {
    if (this.unsavedNow === on) return
    this.unsavedNow = on
    // A Leave that armed while there was something to lose, and then a Keep landed: the next
    // press should leave at once, not confirm a warning that is no longer true.
    if (!on && this.armedItem === 'leaveBuild') this.disarm()
  }

  get unsavedBuild(): boolean {
    return this.unsavedNow
  }

  private unsavedNow = false

  /**
   * The draft differs from the machine Build began on (the 1.0.115 round): Reset arms and confirms,
   * and with nothing to reset its press is reported at once, for the shell to say so. Told every
   * frame, like `unsavedBuild`.
   */
  set resettableBuild(on: boolean) {
    if (this.resettableNow === on) return
    this.resettableNow = on
    if (!on && this.armedItem === 'resetMachine') this.disarm()
  }

  get resettableBuild(): boolean {
    return this.resettableNow
  }

  private resettableNow = false

  /**
   * One frame of gesture input.
   *
   * `palmUp` is the dot product of the left palm's normal with world up, or **`undefined` when
   * there is no tracked left hand at all** — and that distinction is the whole of a bug this
   * caught. A controller player has no palm, so a caller that reported "not up" for "not there"
   * had the menu close itself on the very frame the grip squeeze opened it: the one input scheme
   * that has no palm gesture was the one that could never keep the menu open.
   *
   * `gripHeld` is either controller's grip button.
   */
  advance(palmUp: number | undefined, gripHeld: boolean, frameMs: number): void {
    if (this.armedItem !== undefined) {
      this.armedMs += frameMs
      // An armed item that is walked away from disarms itself, which is the whole point of a
      // window: a player who meant it presses again, and a player who did not, does not.
      if (this.armedMs >= ARM_WINDOW_MS) this.disarm()
    }

    // Legacy gesture replay remains available, but explicitly opened host sessions have one
    // visibility owner. Normal navigation is never controlled by a hand posture or grip.
    if (this.explicitSession || this.presentationNow === 'normal') return

    if (gripHeld && !this.gripWasDown) {
      this.toggle()
      this.openedByGrip = this.openNow
    }
    this.gripWasDown = gripHeld

    // No hand to read: leave the menu exactly as it is. Whatever opened it is what closes it.
    if (palmUp === undefined || Number.isNaN(palmUp)) return

    // **Below the exit threshold is the one thing that clears the latch.** A palm that has
    // genuinely been turned over is the player letting go of the menu; until that happens, a
    // menu closed from the inside stays closed. Checked before the open branch so a single
    // frame can both release the latch and begin a new dwell, which is what a hand flipping
    // down and back up actually does.
    if (palmUp < PALM_UP_EXIT) this.suppressed = false

    if (palmUp >= PALM_UP_ENTER) {
      // Suppressed means the player just chose something with their palm still up. Holding it
      // there is not a request to reopen — it is where their hand happens to be.
      if (this.suppressed) {
        this.palmMs = 0
        return
      }
      this.palmMs += frameMs
      if (this.palmMs >= PALM_HOLD_MS && !this.openNow) this.openNow = true
      return
    }

    // Between the two thresholds is the dead band: not open enough to start a dwell, not
    // turned over enough to close what is already showing. Nothing happens, which is the point.
    if (palmUp >= PALM_UP_EXIT) return

    // Turning the palm back down closes it — but only a menu the *palm* opened. There is no
    // separate close gesture and there must not be: a menu you have to dismiss is a menu in the
    // way of a ball that is still in play.
    this.palmMs = 0
    if (this.openNow && this.armedItem === undefined && !this.openedByGrip) this.openNow = false
  }

  /** Open or close it. The grip's behaviour, and what a shell button does. */
  toggle(): void {
    this.presentationNow = 'quick'
    this.explicitSession = false
    this.openNow = !this.openNow
    this.palmMs = 0
    this.openedByGrip = false
    if (!this.openNow) {
      this.pageNow = this.homeNow
      this.disarm()
    }
  }

  close(): void {
    this.openNow = false
    this.palmMs = 0
    this.openedByGrip = false
    // **Latched here and not in `toggle`.** This is the menu closing itself — a selection was
    // made, or the shell dismissed it — and the palm is almost certainly still up, holding the
    // panel that was just used. `toggle` is a deliberate press by a player who wants the
    // opposite of what they have, and a latch there would make the grip button feel broken.
    this.suppressed = true
    // **Reopened on the home list, always.** A menu that remembered the machine list would open
    // on it the next time a player turned their palm up mid-ball, which is the one moment they
    // are reaching for Pause. (The home list is `root`, or `build` while Build mode is on.)
    this.pageNow = this.homeNow
    this.disarm()
  }

  /**
   * The player selected an item.
   *
   * A non-destructive item selects immediately and closes the menu — every one of them is a
   * thing you do and then get back to the ball. A destructive item arms on the first press and
   * fires on the second, and selecting *anything else* while one is armed disarms it, because
   * moving to another item is the clearest possible statement of "not that one".
   *
   * **A `TOGGLES` item is the exception that stays open** (M8-09): it changes a state its own row
   * displays, and the changed label is the entire feedback for the press. It still disarms
   * anything that was armed, for the same reason every other selection does.
   */
  select(item: MenuItem, now = 0): MenuSelection | undefined {
    if (this.pageNow === 'departure' && ['keep','leaveBuild','back','close'].includes(item)) return {item, confirmed:true}
    if (item === 'close') { this.close(); return { item, confirmed: false } }
    void now
    // Leave build is destructive exactly while there is something to lose (M13-15).
    // …and Reset while there is something to reset (the 1.0.115 round).
    if (DESTRUCTIVE.has(item) || (item === 'resetMachine' && this.resettableNow)) {
      if (this.armedItem === item) {
        this.disarm()
        return { item, confirmed: true }
      }
      this.armedItem = item
      this.armedMs = 0
      return undefined
    }
    this.disarm()
    // **The Build row pages while the mode is on and selects when it is not** (M13-09). Off, it is
    // a request the shell performs — enter the mode — and reports like any other row. On, the mode
    // is already entered and the only thing left to do is show its page, which is a page change
    // and therefore, by the rule three lines down, not a selection.
    // …and while a test runs the home is the test page, which the same row reaches (M13-25).
    // `back` goes one level up now that pages nest (the 1.0.111 round, `backFrom`).
    const page =
      item === 'build' && (this.homeNow === 'build' || this.homeNow === 'test')
        ? this.homeNow
        : item === 'back'
          ? backFrom(this.pageNow)
          : PAGING.get(item)
    if (page !== undefined) {
      // **A page change is not a selection.** Nothing outside the menu happens, so nothing outside
      // the menu is told: a shell that received `bays` would have to know to ignore it, and the
      // one that forgot would navigate away from the room the player was choosing a machine in.
      this.pageNow = page
      return undefined
    }
    // Quick lifetime belongs to the raised-hand host; normal navigation closes only explicitly.
    return { item, confirmed: false }
  }

  private disarm(): void {
    this.armedItem = undefined
    this.armedMs = 0
  }
}

/** Everything a label needs to know about the world outside the menu. */
export interface MenuState {
  /** Cabinet-owned frozen Full layout. Quick always retains its square layout. */
  readonly compactMenu?: boolean
  /** Actual room-placement capability; omitted only by older fixtures. */
  readonly roomAvailable?: boolean
  readonly currentMachineTitle?: string
  readonly voiceAvailable?: boolean
  readonly pendingMenuDetail?: string
  readonly paused: boolean
  /**
   * The machine is asking for three characters right now (M2-19a).
   *
   * **The one field here that decides a *row* rather than a label**, and it is here anyway,
   * because this is already the bag of "what the machine is doing" and the panel's paint and its
   * hit test both need the same answer. They call `rowsFor` separately — a panel that drew one
   * list and tested against another would draw Pause and select Comfort — so the answer has to
   * come from one place, and this is the place both of them already read.
   */
  readonly enteringInitials: boolean
  /**
   * The machine's own sound is off (07 §7).
   *
   * **The row said "Sound" and nothing else**, while the two mute rows below it stated their
   * condition — the rule M8-09 wrote, and the reason it wrote it. That was survivable while the
   * mute lasted only as long as the page; now that it persists, a player can come back to a
   * silent machine and read a row that tells them nothing about why.
   */
  readonly soundMuted: boolean
  /**
   * How loud everything is, before the four group controls (07 §7, 06 §5's "master volume").
   *
   * Here for the reason `soundMuted` is: the row states its condition. `AudioLevel` rather than a
   * number because that is the shape the whole product stores — three named steps, so a wrist row
   * can be a cycle and a settings card can be three buttons, both reading one value.
   */
  readonly master: AudioLevel
  /** Whether a spoken callout also appears as text (NFR-022, 06 §5). */
  readonly captions: boolean
  /** This player's microphone is off (M8-09). */
  readonly micMuted: boolean
  /**
   * What the browser said about a microphone (M8-10).
   *
   * Separate from `micMuted` because the two are different sentences to a player. Both mean
   * "nobody can hear me"; only one of them is undone by pressing the row.
   */
  readonly micAccess: MicrophoneAccess
  /** Everybody else's voices are off (M8-09). */
  readonly voiceMuted: boolean
  /**
   * What each bay's row says, in bay order (M8-20).
   *
   * Labels rather than occupancy, because who is at a machine is `bayNavigation`'s sentence to
   * write and the menu's job is to draw a row. Empty for a session with no venue — mixed reality
   * is one cabinet in a living room and has no row to draw.
   */
  readonly bays: readonly string[]
  /**
   * The machine is at tabletop scale rather than full size (06 §5).
   *
   * Here for the same reason `soundMuted` is: the row states its condition, so the row needs to be
   * told what the condition is. A "Tabletop" row that means "press to get tabletop" and a
   * "Tabletop" row that means "you are in tabletop" are the same four rows apart.
   */
  readonly tabletop: boolean
  /**
   * There is a machine in the room for the game rows to be about (M8-63).
   *
   * **Optional, and `undefined` means yes**, which is the C# field's `= true` default written the
   * way a structural type writes it. Read as `machineInPlay !== false` so a state literal that
   * predates this field keeps the rows it has always had.
   *
   * See `rowsFor`'s parameter of the same name for what a menu without it did.
   */
  readonly machineInPlay?: boolean
  /** Actual scored phase; omitted only by legacy fixtures. */
  readonly gameInProgress?: boolean
  readonly buildDeparture?: boolean
  /**
   * Comfort mode is on — the machine has been repositioned for a seated player (06 §6, XR-007).
   *
   * The state the root `comfort` row never showed. It toggled seated mode and said "Comfort", so a
   * player who had switched it on and then looked at the menu read a row that could not tell them
   * whether it was.
   */
  readonly seated: boolean
  readonly seatingChoiceKnown?: boolean
  /** The player's answer to reduce flashing, including "follow the system" (NFR-023, 06 §6). */
  readonly flashing: FlashingChoice
  /** How much of the machine's own camera motion the player wants, as one of `MOTION_STEPS`. */
  readonly motion: number
  /** How far the machine sits from where it would otherwise go (06 §6's adjustable height). */
  readonly heightOffset: HeightOffset
  /**
   * Build mode's rows, when Build mode is offered at all (M13-09).
   *
   * **Absent means the flag is off**, and every state literal written before this field keeps the
   * rows it had — the `machineInPlay` shape, for the same reason. Present, the root list gains its
   * Build row and the four fields are what the Build page's rows say.
   */
  readonly build?: BuildMenuState
  /**
   * Which mode the wrist can switch to, while the runtime has the other one (14 Sep 2026).
   *
   * **Absent means no row**, the `build` shape: a state literal that predates this field, and
   * every state the native fixture replays, draws the menu it always drew. `'vr'` in MR, `'mr'`
   * in VR.
   */
  readonly switchTo?: SwitchTarget
}

/** What the Build page's rows state (M13-09). */
export interface BuildMenuState {
  readonly unfinishedMove?: boolean
  readonly testPreparing?: boolean
  /** The mode is on: the root row reads as a way back to the page rather than a way in. */
  readonly active: boolean
  readonly canUndo: boolean
  readonly canRedo: boolean
  /**
   * What the next Undo and Redo would change, in words — a part's id, or "3 parts" (the 1.0.115
   * round) — so the row says "Undo · flipper1". Absent draws the bare "Undo" / "Redo".
   */
  readonly undoWhat?: string
  readonly redoWhat?: string
  /** The draft differs from the machine Build began on: Reset has something to do. Absent reads as yes. */
  readonly canReset?: boolean
  readonly symmetry: boolean
  /** A test is running, so the row offers the way back. */
  readonly testing: boolean
  /** The test tools' states (M13-25), while a test is running; absent draws the rows as if none is on. */
  readonly frozen?: boolean
  /** Frozen only because a menu was opened over the test (1.0.245): the way back does not say "frozen". */
  readonly frozenForMenu?: boolean
  readonly speed?: number
  readonly placing?: boolean
  readonly adding?: boolean
  readonly trail?: boolean
  /**
   * The language the Build rows read in (M13-26): the bench's own table, English absent. Only
   * these rows — the rest of the menu is the product's English on every client until the day
   * the whole menu is translated, which is a larger change than Build mode's.
   */
  readonly language?: BenchLanguage
}

/**
 * The label an item shows, given whether it is armed.
 *
 * Here rather than in the shell because the *armed* label is a safety property: it has to say
 * what the second press will do, in words, and the place that decides an item is destructive is
 * the place that should be made to write that sentence.
 *
 * **The mute rows state the condition rather than the action**, which is the opposite of every
 * other row here and is deliberate (M8-09). "Mute" on a row that is already muted is the most
 * common way to build a mute control that lies: the player reads it, believes it describes what
 * is happening, and talks. `voiceMute.ts` owns those two sentences so the HUD says the same
 * words.
 */
/** The bench's words for the Build rows, in the language the state names (M13-26). */
/** Shared titles and hierarchy cues describe the same destinations on both clients. */
export function headerFor(view: MenuView): string {
  if (view.presentation === 'quick') return view.page === 'build' ? 'QUICK / BUILDER' : view.page === 'test' ? 'QUICK / TEST PLAY' : view.page === 'time' ? 'QUICK / TEST TOOLS' : 'QUICK CONTROLS'
  const titles: Record<MenuPage,string> = {root:'FULL MENU',game:'CURRENT TABLE',departure:'LEAVE BUILDER?',build:'BUILDER CONTROLS',test:'TEST CONTROLS',time:'TEST TOOLS',settings:'SETTINGS',sound:'AUDIO',comfort:'COMFORT & SEATING',place:'ROOM & POSITION',position:'POSITION',buildOptions:'SYMMETRY & RESET',help:'HELP',bays:'ARCADE BAYS'}
  return titles[view.page]
}
export function activityLabel(state: MenuState): string {
  return state.build?.active ? state.build.testing ? `Test play · ${state.build.frozen ? 'frozen' : 'running'} · unscored` : 'Builder draft · editing'
    : state.machineInPlay === false ? 'Arcade · no table' : state.enteringInitials ? 'Game · initials' : state.gameInProgress === false ? 'Table · ready to start' : state.paused ? 'Table · paused' : 'Table · running'
}
export function footerFor(view: MenuView,state: MenuState): string {
  if(state.pendingMenuDetail)return state.pendingMenuDetail
  if(view.presentation==='quick')return 'Lower your left hand to close'
  if(view.page==='build')return 'Keep draft saves an account copy · Settings / Account'
  if(view.page==='comfort')return 'Hands: cabinet controls · Controllers: shortcuts'
  if(view.page==='root'&&!state.build?.active&&!state.seatingChoiceKnown)return 'Playing seated? Settings / Comfort & seating'
  return 'Close returns without resuming'
}
export function primaryItem(view: MenuView,state: MenuState): MenuItem | undefined {
  if (view.presentation === 'quick' || view.page !== 'root') return undefined
  return state.build?.active ? 'close' : state.machineInPlay !== false ? state.enteringInitials ? 'skipInitials' : 'pauseResume' : 'otherMachine'
}

/** The production inventory used by every painter, hit test and cache identity. */
export function contextRows(view: MenuView, state: MenuState): readonly MenuItem[] {
  const presentation = view.presentation ?? 'normal'
  const rows = [...rowsFor(view.page, state.bays.length, state.enteringInitials, state.machineInPlay !== false,
    state.build !== undefined, state.switchTo, presentation, state.voiceAvailable === true)]
  if (presentation === 'quick') return rows.filter(item => !(item === 'pauseResume' && state.gameInProgress === false))
  const active = state.build?.active === true
  const filtered = rows.filter(item => !(item === 'endGame' && (state.gameInProgress === false || state.enteringInitials)) && !(active && (item === 'pauseResume' || item === 'skipInitials' || item === 'endGame')) && !(item === 'game' && (active || state.machineInPlay === false)))
  if (view.page === 'root' && active) return ['close', ...filtered.filter(item => item !== 'close')]
  return filtered.includes('close') ? filtered : [...filtered, 'close']
}

/** Undefined means usable. Hosts repeat this check so stale rows cannot bypass it. */
export function unavailableReason(item: MenuItem, state: MenuState): string | undefined {
  const b = state.build, active = b?.active === true, testing = active && b?.testing === true
  if (item === 'endGame' && (state.gameInProgress === false || state.enteringInitials)) return 'There is no running game to end.'
  if (item === 'machine' || bayOfItem(item) >= 0) { if (state.bays.length === 0) return 'Arcade bays are available in a connected VR arcade.' }
  if (active && (item === 'pauseResume' || item === 'skipInitials' || item === 'endGame'))
    return 'Use Builder or Test controls; your scored game is kept.'
  if ((item === 'muteMic' || item === 'muteVoice') && state.voiceAvailable !== true)
    return 'Voice controls are available in a connected arcade.'
  if (active && ['scaleMode','faceMe','nudgeLeft','nudgeRight','replaceMachine','forgetSpot','seatedMode','heightOffset'].includes(item))
    return 'Leave Builder before changing the cabinet placement.'
  if (state.roomAvailable === false && ['scaleMode','faceMe','nudgeLeft','nudgeRight','replaceMachine','forgetSpot'].includes(item))
    return 'Available in mixed reality with room placement.'
  if (active && b?.unfinishedMove && ['undo','redo','keep','resetMachine'].includes(item))
    return 'Finish or cancel the held move first.'
  if (['undo','redo','testPlay','keep','leaveBuild','symmetry','resetMachine'].includes(item) && !active) return 'Open Builder to use this control.'
  if (testing && ['undo','redo','symmetry','keep','resetMachine'].includes(item) && !(item === 'keep' && state.buildDeparture)) return 'Return to editing to change or keep the draft.'
  if (item === 'undo' && !b?.canUndo) return 'Nothing to undo.'
  if (item === 'redo' && !b?.canRedo) return 'Nothing to redo.'
  if (item === 'resetMachine' && b?.canReset === false) return 'Nothing to reset.'
  if (item === 'testPlay' && !testing && b?.unfinishedMove) return 'Finish placing the part before Test play.'
  if (item === 'testPlay' && b?.testPreparing) return 'Preparing Test play…'
  if (['freeze','step','slowMotion','placeBall','addBall','trail'].includes(item) && !testing) return 'Available during Test play.'
  if (item === 'step' && b?.frozen !== true) return 'Freeze the test before stepping.'
  return undefined
}

function bench(state: MenuState): ReturnType<typeof benchStringsFor> {
  return benchStringsFor(state.build?.language ?? 'en')
}

export function labelFor(item: MenuItem, armed: boolean, state: MenuState, page: MenuPage = 'root'): string {
  if (page === 'departure') {
    if (item === 'keep') return 'Keep draft and leave'
    if (item === 'leaveBuild') return 'Discard draft and leave'
    if (item === 'back' || item === 'close') return state.build?.testing ? 'Return to test' : 'Return to editing'
  }
  if (item === 'close') return state.build?.active ? state.build.testing
    ? state.build.frozen === true && state.build.frozenForMenu !== true ? 'Return to test · frozen' : 'Return to test'
    : 'Return to editing' : state.paused && state.gameInProgress !== false ? 'Close menu · game paused' : 'Close menu'
  if (item === 'endGame') return armed ? 'Press again to end the game' : 'End game'
  if (item === 'forgetSpot') {
    return armed ? 'Press again to forget this spot' : 'Forget this spot'
  }
  switch (item) {
    case 'pauseResume':
      return state.gameInProgress === false ? 'Return to table' : state.paused ? 'Resume' : 'Pause'
    case 'machine':
      return 'Arcade bays'
    case 'scaleAndPlace':
      return 'Room & position'
    case 'game':
      return 'Current table'
    case 'settings':
      // Carries M5-34's sentence out to the root now that the Sound row is a page further in: a
      // player coming back to a machine they muted finds the reason without opening anything.
      return state.soundMuted ? 'Settings · sound off' : 'Settings'
    case 'position':
      return 'Position'
    case 'time':
      return 'Test tools'
    case 'buildOptions':
      return 'Symmetry & reset'
    case 'fullMenu': return 'Full menu'
    case 'currentRules': return 'Rules & goals'
    case 'help': return 'Help'
    case 'tutorial': return state.build?.active ? state.build.testing ? 'Test play guide' : 'Builder guide' : 'How to play'
    case 'account': return 'Account'
    case 'comfort':
      // **A page name that reports the one condition worth seeing from outside it**, exactly as
      // `sound` does and for the same reason: pressing it opens a list, so a row reading "Seated"
      // would look like a switch it is not. It says nothing while the machine sits where it always
      // has, and says so when a player has moved it.
      return state.seated ? 'Comfort · seated' : 'Comfort & seating'
    case 'seatedMode':
      // Name the seated-play setting and its current state, rather than only the posture.
      return state.seated ? 'Seated play: on' : 'Seated play: off'
    case 'reduceFlashing':
      return flashingLabel(state.flashing)
    case 'reduceMotion':
      return motionLabel(state.motion)
    case 'heightOffset':
      return heightLabel(state.heightOffset)
    case 'sound':
      // **A page name that still reports the one condition worth seeing from outside it** (M5-34).
      // Every other row here states its own state because pressing it changes that state; this one
      // opens a list, so "Sound on" would read as a switch it is not. It says nothing while the
      // machine is audible and says so when it is not — which is the case M5-34 was written
      // about: a player coming back to a machine they muted, and finding no reason on the row.
      //
      // The master sitting at `off` is deliberately *not* folded in. `muteAll`, one press behind
      // this, reads "Sound on" in that state, and two rows contradicting each other is worse than
      // one row saying less.
      return state.soundMuted ? 'Audio · game muted' : 'Audio'
    case 'muteAll':
      // This output toggle is distinct from microphone and incoming-voice controls.
      return `Game ${soundLabel(state.soundMuted).toLowerCase()}`
    case 'masterVolume':
      return masterLabel(state.master)
    case 'captions':
      return captionsLabel(state.captions)
    case 'muteMic':
      return state.voiceAvailable === false ? 'Microphone unavailable' : micLabel(state.micMuted, state.micAccess)
    case 'muteVoice':
      return state.voiceAvailable === false ? 'Voices unavailable' : state.voiceMuted ? 'Other voices off' : 'Other voices on'
    case 'scaleMode':
      // States the condition, like the mute rows above and for the same reason.
      return state.tabletop ? 'Tabletop' : 'Full size'
    case 'faceMe':
      return 'Face me'
    case 'recenterMachine':
      return 'Bring table here'
    case 'nudgeLeft':
      return 'Turn left 5°'
    case 'nudgeRight':
      return 'Turn right 5°'
    case 'replaceMachine':
      return 'Place it again'
    case 'exitXr':
      return 'Exit XR'
    // Named for where it goes, not for what it does: "Switch mode" on a wrist says nothing a
    // player can decide on, and the row is only ever drawn with one place to go.
    case 'switchToVr':
      return 'Switch to VR'
    case 'switchToMr':
      return 'Switch to MR'
    case 'otherMachine':
      return 'Choose table'
    case 'back':
      return `Back to ${{root:'Full menu',settings:'Settings',place:'Room',position:'Position',test:'Test controls',build:'Builder controls'}[backFrom(page) as 'root'|'settings'|'place'|'position'|'test'|'build'] ?? 'Full menu'}`
    case 'skipInitials':
      // Says what it does to the score, because "Skip" alone on a headset — where the row is read
      // rather than tapped past — could as easily mean skipping the game.
      return 'Skip initials'
    // Build mode (M13-09). Each row that has a condition states it, which is the rule every other
    // page here obeys; the two that cannot act say so in words rather than greying out, because a
    // greyed row on a wrist is a row a player presses repeatedly to find out why.
    case 'build':
      if(state.build?.active)return state.build.language==='fr'?(state.build.testing?'Commandes de test':'Commandes Construire'):(state.build.testing?'Test controls':'Builder controls')
      return state.build?.active === true ? bench(state)['menu.buildMenu'] : state.build?.language === 'fr' ? 'Atelier' : 'Builder'
    case 'undo':
      if (state.build?.canUndo !== true) return bench(state)['menu.nothingToUndo']
      return state.build.undoWhat === undefined ? bench(state)['menu.undo'] : benchText(bench(state), 'menu.undoWhat', { what: state.build.undoWhat })
    case 'redo':
      if (state.build?.canRedo !== true) return bench(state)['menu.nothingToRedo']
      return state.build.redoWhat === undefined ? bench(state)['menu.redo'] : benchText(bench(state), 'menu.redoWhat', { what: state.build.redoWhat })
    case 'resetMachine':
      if (armed) return bench(state)['menu.resetArmed']
      return state.build?.canReset === false ? bench(state)['menu.nothingToReset'] : bench(state)['menu.reset']
    case 'symmetry':
      return state.build?.symmetry === false ? bench(state)['menu.symmetryOff'] : bench(state)['menu.symmetryOn']
    case 'testPlay':
      if (!state.build?.testing && state.build?.unfinishedMove) return state.build.language === 'fr' ? 'Terminez la pose' : 'Finish placing first'
      if (state.build?.testPreparing) return state.build.language === 'fr' ? 'Préparation du test' : 'Preparing Test play'
      return state.build?.testing === true ? bench(state)['menu.backToBuild'] : bench(state)['menu.testPlay']
    // The test tools (M13-25). Each row states the condition it flips, as the Build rows do, and
    // the one that cannot act says why in words.
    case 'freeze':
      return state.build?.frozen === true ? bench(state)['menu.run'] : bench(state)['menu.freeze']
    case 'step':
      return state.build?.frozen === true ? bench(state)['menu.step'] : bench(state)['menu.stepFrozenFirst']
    case 'slowMotion':
      return benchText(bench(state), 'menu.speed', { speed: speedText(state.build?.speed ?? 1) })
    case 'placeBall':
      return state.build?.placing === true ? bench(state)['menu.placeBallArmed'] : bench(state)['menu.placeBall']
    case 'addBall':
      return state.build?.adding === true ? bench(state)['menu.addBallArmed'] : bench(state)['menu.addBall']
    case 'trail':
      return state.build?.trail === false ? bench(state)['menu.trailOff'] : bench(state)['menu.trailOn']
    case 'keep':
      return state.build?.language === 'fr' ? bench(state)['menu.keep'] : 'Keep draft'
    case 'leaveBuild':
      // Armed only while the bench has unkept edits (M13-15); the sentence says what the second
      // press costs, as the other armed rows do.
      return armed ? bench(state)['menu.leaveArmed'] : bench(state)['menu.leaveBuild']
    default:
      // A bay row. Its label is the room's, and an empty one is a bay this session has no venue
      // for — drawn as the bare machine number rather than as a blank row.
      return state.bays[bayOfItem(item)] ?? `Machine ${bayOfItem(item) + 1}`
  }
}
